import { Injectable } from "@angular/core";
import * as SignaloidClient from "@signaloid/cloud-compute-engine-client";
import { CreateSourceBuildRequest } from "@signaloid/cloud-compute-engine-client";
import { HttpClient } from "@angular/common/http";
import { environment } from "@env";
import { catchError, EMPTY, from, mergeMap, tap } from "rxjs";
import { SnackbarService } from "design-system";
import { Router } from "@angular/router";

@Injectable({
	providedIn: "root",
})
// This service is made in order to maintain state of signaloid client
export class SignaloidWrapperService {
	private signaloidClient = SignaloidClient.createClient(
		{ method: "email" },
		{
			overrideEndpoints: {
				api: environment.API_URL,
				websocket: environment.REALTIME_ENDPOINT,
				host: environment.REALTIME_HOST,
			},
		},
	);
	constructor(
		private http: HttpClient,
		private snackbarService: SnackbarService,
		private router: Router,
	) {
		console.log(this.signaloidClient);
	}

	public getBuildsFor(repoId: string) {
		return from(this.signaloidClient.repositories.getBuilds(repoId));
	}

	public getCurrentUser() {
		return this.signaloidClient.users.me();
	}

	public userSRPLogin(username: string, password: string) {
		return this.signaloidClient.auth.login(username, password);
	}

	public getUserIdForAuth() {
		return this.signaloidClient.auth.getUserId();
	}

	public getCurrentUserDetails() {
		return from(this.signaloidClient.users.me()).pipe(
			catchError((err) => {
				if (err.message.includes("Failed to retrieve ID token")) {
					this.snackbarService.openSnackbar({
						header: "Error",
						type: "error",
						description: "Authorization failed. Please log in again.",
					});
				}
				this.router.navigate(["/login"]);
				return EMPTY;
			}),
		);
	}

	public listUserRepositories() {
		return this.signaloidClient.repositories.list();
	}

	public getRepository(repositoryId: string) {
		return this.signaloidClient.repositories.getOne(repositoryId);
	}

	public registerRepositoryToCurrentUser(repo: any) {
		return this.signaloidClient.repositories.connect(repo);
	}

	public async getRepositoryByUrl(repositoryUrl: string) {
		const repos = await this.listUserRepositories();
		return repos.Repositories.find((elem) => elem.RemoteURL === repositoryUrl);
	}

	private async getDataSourceForDemos() {
		const user = await this.getCurrentUser();
		return {
			Location: "sd0",
			ResourceID: `signaloid-cloud-storage:/${user.UserID}`,
			ResourceType: "SignaloidCloudStorage",
		};
	}

	public buildRepository(repositoryId: string, coreId: string) {
		return from(this.signaloidClient.builds.createFromRepository(repositoryId, { CoreID: coreId }));
	}

	public startTask(buildId: string, args: string, authFree: boolean = false) {
		if (authFree) return this.authFreeGetTasks(buildId, args);
		return from(this.signaloidClient.tasks.createTask(buildId, { Arguments: args }));
	}

	public getTaskOutputs(taskId: string, authfree: boolean = false) {
		if (authfree) return this.authFreeGetTaskOutputs(taskId);
		return this.signaloidClient.tasks.getOutputURLs(taskId);
	}

	public getUserTasks() {
		return from(this.signaloidClient.tasks.list());
	}

	public listFiles() {
		return from(this.signaloidClient.files.list());
	}

	public getFileLocationToUpload(filename: string, file: Blob) {
		return from(this.signaloidClient.files.upload(filename, file));
	}

	public buildFromSourceCode(payload: CreateSourceBuildRequest) {
		return from(this.signaloidClient.builds.createFromSourceCode(payload));
	}

	public getUserTier() {
		return from(this.signaloidClient.subscriptions.get());
	}

	private authFreeGetTasks(buildId: string, args: string) {
		return this.http
			.post<any>(`${environment.API_URL}/builds/${buildId}/tasks/public`, { Arguments: args })
			.pipe(tap((res) => console.log(res)));
	}

	private authFreeGetTaskOutputs(taskId: string) {
		return this.http
			.get<any>(`${environment.API_URL}/tasks/${taskId}/outputs/public`)
			.pipe(tap((res) => console.log(res)));
	}
	refreshAccessToken() {
		return this.signaloidClient.auth.refreshAccessToken();
	}
	public updateCoreInRepository(repositoryID: string, coreID: string) {
		return this.signaloidClient.repositories.update(repositoryID, {
			Core: coreID,
		});
	}
}
